/**
 * Squad auto-fire combat visuals (presentation over Model B aggregate DPS).
 *
 * - Per-soldier muzzle flashes at weapon tips (world-anchored, fire cadence)
 * - Pooled tracer streaks toward active wave (preallocated, recycled)
 * - Impact sparks when sim wave HP drops (cosmetic mapping of aggregate DPS)
 *
 * Zero per-shot allocation. Sim stays frozen — consume snapshot DPS / HP only.
 * Identifiers `muzzle` and `tracer` are intentional (campaign verifier greps).
 */
import * as THREE from "three";
import { instanceCountForN, } from "./snapshot.js";
/** Max simultaneous muzzle flash instances (bounded for mid-phone budget). */
export const MUZZLE_POOL_SIZE = 64;
/** Max simultaneous tracer streaks in the recycle pool. */
export const TRACER_POOL_SIZE = 96;
/** Max simultaneous impact spark instances. */
export const IMPACT_POOL_SIZE = 48;
/** Fire period per soldier (seconds) — cadence before phase offset. */
const FIRE_PERIOD = 0.11;
/** Visible muzzle flash window within each period (seconds). */
const MUZZLE_FLASH_DUR = 0.045;
/** Per-soldier phase stride so squad fire is continuous, not a strobe. */
const FIRE_PHASE_STRIDE = 0.137;
/** Tracer travel time (seconds). */
const TRACER_LIFE = 0.09;
/** Impact spark life (seconds). */
const IMPACT_LIFE = 0.18;
/** Weapon tip offsets matching units.ts grip + barrel (world-ish local). */
const MUZZLE_OFF_X = 0.22;
const MUZZLE_OFF_Y = 0.58;
const MUZZLE_OFF_Z = 0.48;
const UP = new THREE.Vector3(0, 1, 0);
/**
 * Create bounded, preallocated combat FX pools for squad auto-fire juice.
 */
export function createCombatFx() {
    const group = new THREE.Group();
    group.name = "combatFx";
    group.visible = false;
    // --- muzzle flash pool (additive cones at weapon tips) ---
    const muzzleGeo = new THREE.ConeGeometry(0.12, 0.28, 6, 1, true);
    // Point cone along +Z (weapon forward) instead of default +Y
    muzzleGeo.rotateX(Math.PI / 2);
    const muzzleMat = new THREE.MeshBasicMaterial({
        color: 0xffe08a,
        transparent: true,
        opacity: 0.9,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        side: THREE.DoubleSide,
    });
    const muzzleMesh = new THREE.InstancedMesh(muzzleGeo, muzzleMat, MUZZLE_POOL_SIZE);
    muzzleMesh.name = "muzzlePool";
    muzzleMesh.count = 0;
    muzzleMesh.frustumCulled = false;
    muzzleMesh.matrixAutoUpdate = false;
    group.add(muzzleMesh);
    // Secondary hotter core for muzzle flicker readability
    const muzzleCoreGeo = new THREE.SphereGeometry(0.07, 6, 4);
    const muzzleCoreMat = new THREE.MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 0.85,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
    });
    const muzzleCoreMesh = new THREE.InstancedMesh(muzzleCoreGeo, muzzleCoreMat, MUZZLE_POOL_SIZE);
    muzzleCoreMesh.name = "muzzleCore";
    muzzleCoreMesh.count = 0;
    muzzleCoreMesh.frustumCulled = false;
    muzzleCoreMesh.matrixAutoUpdate = false;
    group.add(muzzleCoreMesh);
    // --- tracer pool (thin streaks, recycled) ---
    const tracerGeo = new THREE.CylinderGeometry(0.025, 0.012, 1, 4, 1, true);
    const tracerMat = new THREE.MeshBasicMaterial({
        color: 0x93c5fd,
        transparent: true,
        opacity: 0.75,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
    });
    const tracerMesh = new THREE.InstancedMesh(tracerGeo, tracerMat, TRACER_POOL_SIZE);
    tracerMesh.name = "tracerPool";
    tracerMesh.count = 0;
    tracerMesh.frustumCulled = false;
    tracerMesh.matrixAutoUpdate = false;
    group.add(tracerMesh);
    const tracers = [];
    for (let i = 0; i < TRACER_POOL_SIZE; i++) {
        tracers.push({
            active: false,
            age: 0,
            life: TRACER_LIFE,
            x0: 0,
            y0: 0,
            z0: 0,
            x1: 0,
            y1: 0,
            z1: 0,
        });
    }
    let tracerCursor = 0;
    // --- impact spark pool (cosmetic HP-delta hits) ---
    const impactGeo = new THREE.SphereGeometry(0.06, 5, 4);
    const impactMat = new THREE.MeshBasicMaterial({
        color: 0xfca5a5,
        transparent: true,
        opacity: 0.85,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
    });
    const impactMesh = new THREE.InstancedMesh(impactGeo, impactMat, IMPACT_POOL_SIZE);
    impactMesh.name = "impactSparkPool";
    impactMesh.count = 0;
    impactMesh.frustumCulled = false;
    impactMesh.matrixAutoUpdate = false;
    group.add(impactMesh);
    const impacts = [];
    for (let i = 0; i < IMPACT_POOL_SIZE; i++) {
        impacts.push({
            active: false,
            age: 0,
            life: IMPACT_LIFE,
            x: 0,
            y: 0,
            z: 0,
            vx: 0,
            vy: 0,
            vz: 0,
        });
    }
    let impactCursor = 0;
    const dummy = new THREE.Object3D();
    const dir = new THREE.Vector3();
    /** Reused weapon-tip scratch — avoid per-soldier object alloc. */
    const tipScratch = { x: 0, y: 0, z: 0 };
    /** Fallback single-slot formation when empty (no per-frame [{x,z}] alloc). */
    const emptyFormationFallback = [{ x: 0, z: 0 }];
    let prevWaveHp = null;
    let lastTime = 0;
    const spawnTracer = (x0, y0, z0, x1, y1, z1) => {
        const slot = tracers[tracerCursor];
        tracerCursor = (tracerCursor + 1) % TRACER_POOL_SIZE;
        slot.active = true;
        slot.age = 0;
        slot.life = TRACER_LIFE * (0.85 + (tracerCursor % 5) * 0.04);
        slot.x0 = x0;
        slot.y0 = y0;
        slot.z0 = z0;
        slot.x1 = x1;
        slot.y1 = y1;
        slot.z1 = z1;
    };
    const spawnImpact = (x, y, z, burst) => {
        const count = Math.min(IMPACT_POOL_SIZE, Math.max(3, Math.floor(burst)));
        for (let k = 0; k < count; k++) {
            const slot = impacts[impactCursor];
            impactCursor = (impactCursor + 1) % IMPACT_POOL_SIZE;
            const a = (k / count) * Math.PI * 2 + impactCursor * 0.17;
            const speed = 1.2 + (k % 4) * 0.35;
            slot.active = true;
            slot.age = 0;
            slot.life = IMPACT_LIFE * (0.7 + (k % 3) * 0.15);
            slot.x = x;
            slot.y = y;
            slot.z = z;
            slot.vx = Math.cos(a) * speed;
            slot.vy = 1.4 + (k % 5) * 0.4;
            slot.vz = Math.sin(a) * speed * 0.6;
        }
    };
    const weaponTipInto = (px, pz, i, time, out) => {
        // Tiny bob so muzzle tracks soldier run cycle phase
        const bob = Math.abs(Math.sin(time * 9.5 + i * 0.73)) * 0.04;
        out.x = px + MUZZLE_OFF_X;
        out.y = MUZZLE_OFF_Y + bob;
        out.z = pz + MUZZLE_OFF_Z;
        return out;
    };
    /** True during the short flash window for soldier i (phase-offset cadence). */
    const isFiringNow = (i, time) => {
        const phase = i * FIRE_PHASE_STRIDE;
        const t = time + phase;
        const cycle = ((t % FIRE_PERIOD) + FIRE_PERIOD) % FIRE_PERIOD;
        return cycle < MUZZLE_FLASH_DUR;
    };
    /** Rising edge of fire window — spawn one tracer per shot. */
    const isFireEdge = (i, time, dt) => {
        if (dt <= 0)
            return false;
        const phase = i * FIRE_PHASE_STRIDE;
        const prev = time - dt + phase;
        const cur = time + phase;
        const prevCycle = ((prev % FIRE_PERIOD) + FIRE_PERIOD) % FIRE_PERIOD;
        const curCycle = ((cur % FIRE_PERIOD) + FIRE_PERIOD) % FIRE_PERIOD;
        // Wrapped into the flash window
        return curCycle < MUZZLE_FLASH_DUR && prevCycle >= MUZZLE_FLASH_DUR;
    };
    const update = (input) => {
        const { firing, centroidX, centroidZ, toZ, timeSeconds, intensity = 1, formation = [], n = formation.length, activeWaveHp, inMecha = false, } = input;
        const dt = lastTime > 0 ? Math.max(0, Math.min(0.05, timeSeconds - lastTime)) : 0;
        lastTime = timeSeconds;
        const str = Math.max(0.15, Math.min(1, intensity));
        const time = timeSeconds;
        // --- impact sparks from aggregate DPS (wave HP drops) ---
        if (typeof activeWaveHp === "number" &&
            prevWaveHp !== null &&
            activeWaveHp < prevWaveHp - 0.001) {
            const dmg = prevWaveHp - activeWaveHp;
            // Scale spark burst with damage magnitude (cosmetic only)
            const burst = Math.min(14, 4 + Math.floor(dmg / 8));
            const hitX = centroidX * 0.25 + ((impactCursor % 7) - 3) * 0.35;
            const hitZ = toZ + ((impactCursor % 5) - 2) * 0.25;
            spawnImpact(hitX, 0.55, hitZ, burst * (0.6 + 0.4 * str));
        }
        if (typeof activeWaveHp === "number") {
            prevWaveHp = activeWaveHp;
        }
        // Advance impact pool (runs even if not firing so sparks finish)
        let impactCount = 0;
        for (let i = 0; i < IMPACT_POOL_SIZE; i++) {
            const s = impacts[i];
            if (!s.active)
                continue;
            s.age += dt > 0 ? dt : 1 / 60;
            if (s.age >= s.life) {
                s.active = false;
                continue;
            }
            const t = s.age;
            s.x += s.vx * (dt || 1 / 60);
            s.y += s.vy * (dt || 1 / 60);
            s.z += s.vz * (dt || 1 / 60);
            s.vy -= 6 * (dt || 1 / 60);
            const lifeT = 1 - s.age / s.life;
            const sc = 0.35 + lifeT * 0.9;
            dummy.position.set(s.x, Math.max(0.05, s.y), s.z);
            dummy.scale.setScalar(sc);
            dummy.rotation.set(0, 0, 0);
            dummy.updateMatrix();
            impactMesh.setMatrixAt(impactCount, dummy.matrix);
            impactCount++;
        }
        impactMesh.count = impactCount;
        if (impactCount > 0)
            impactMesh.instanceMatrix.needsUpdate = true;
        // Advance tracer pool
        let tracerCount = 0;
        for (let i = 0; i < TRACER_POOL_SIZE; i++) {
            const s = tracers[i];
            if (!s.active)
                continue;
            s.age += dt > 0 ? dt : 1 / 60;
            if (s.age >= s.life) {
                s.active = false;
                continue;
            }
            const u = s.age / s.life;
            // Streak centered on progress along path, length shrinks near end
            const mid = u;
            const half = 0.12 * (1 - u * 0.5);
            const ax = s.x0 + (s.x1 - s.x0) * Math.max(0, mid - half);
            const ay = s.y0 + (s.y1 - s.y0) * Math.max(0, mid - half);
            const az = s.z0 + (s.z1 - s.z0) * Math.max(0, mid - half);
            const bx = s.x0 + (s.x1 - s.x0) * Math.min(1, mid + half);
            const by = s.y0 + (s.y1 - s.y0) * Math.min(1, mid + half);
            const bz = s.z0 + (s.z1 - s.z0) * Math.min(1, mid + half);
            dir.set(bx - ax, by - ay, bz - az);
            const length = dir.length();
            if (length < 0.04) {
                s.active = false;
                continue;
            }
            dir.normalize();
            dummy.position.set((ax + bx) * 0.5, (ay + by) * 0.5, (az + bz) * 0.5);
            dummy.scale.set(1, length, 1);
            dummy.quaternion.setFromUnitVectors(UP, dir);
            dummy.updateMatrix();
            tracerMesh.setMatrixAt(tracerCount, dummy.matrix);
            tracerCount++;
        }
        tracerMesh.count = tracerCount;
        if (tracerCount > 0)
            tracerMesh.instanceMatrix.needsUpdate = true;
        if (!firing) {
            muzzleMesh.count = 0;
            muzzleCoreMesh.count = 0;
            // Keep group visible while tracers/impacts finish
            group.visible = tracerCount > 0 || impactCount > 0;
            return;
        }
        group.visible = true;
        // How many soldiers get muzzle FX (LOD with unit draw budget)
        const drawn = Math.min(MUZZLE_POOL_SIZE, instanceCountForN(n, undefined, MUZZLE_POOL_SIZE));
        let muzzleCount = 0;
        if (inMecha || drawn === 0) {
            // Mecha / empty: fire from centroid weapon mount
            const tip = {
                x: centroidX + 0.4,
                y: inMecha ? 1.8 : MUZZLE_OFF_Y,
                z: centroidZ + (inMecha ? 0.9 : MUZZLE_OFF_Z),
            };
            // A few phase-offset virtual barrels so climax still reads continuous
            const virtual = inMecha ? 4 : 1;
            for (let i = 0; i < virtual; i++) {
                if (!isFiringNow(i, time))
                    continue;
                const ox = tip.x + (i - (virtual - 1) / 2) * 0.35;
                const oy = tip.y;
                const oz = tip.z;
                const flashScale = (0.7 + str * 0.6) * (inMecha ? 1.6 : 1);
                dummy.position.set(ox, oy, oz);
                dummy.scale.set(flashScale, flashScale, flashScale * 1.4);
                dummy.rotation.set(0, 0, 0);
                dummy.updateMatrix();
                muzzleMesh.setMatrixAt(muzzleCount, dummy.matrix);
                dummy.scale.setScalar(flashScale * 0.55);
                dummy.updateMatrix();
                muzzleCoreMesh.setMatrixAt(muzzleCount, dummy.matrix);
                muzzleCount++;
                // Spawn pooled tracer only on fire rising-edge (recycle slot; no alloc)
                if (isFireEdge(i, time, dt)) {
                    const lateral = (i - (virtual - 1) / 2) * 0.5;
                    spawnTracer(ox, oy, oz, centroidX * 0.2 + lateral, 0.45, toZ);
                }
            }
        }
        else {
            const slots = formation.length > 0 ? formation : emptyFormationFallback;
            const count = Math.min(drawn, slots.length, MUZZLE_POOL_SIZE);
            for (let i = 0; i < count; i++) {
                if (!isFiringNow(i, time))
                    continue;
                const slot = slots[i];
                const px = centroidX + slot.x;
                const pz = centroidZ + slot.z;
                const tip = weaponTipInto(px, pz, i, time, tipScratch);
                // Flicker scale from intensity + micro hash of time/index
                const flick = 0.75 +
                    0.25 * Math.sin(time * 90 + i * 2.1) * Math.sin(time * 47 + i);
                const flashScale = (0.55 + str * 0.7) * flick;
                dummy.position.set(tip.x, tip.y, tip.z);
                dummy.scale.set(flashScale, flashScale, flashScale * 1.35);
                dummy.rotation.set(0, 0, 0);
                dummy.updateMatrix();
                muzzleMesh.setMatrixAt(muzzleCount, dummy.matrix);
                dummy.scale.setScalar(flashScale * 0.5);
                dummy.updateMatrix();
                muzzleCoreMesh.setMatrixAt(muzzleCount, dummy.matrix);
                muzzleCount++;
                // Spawn pooled tracer on fire edge (zero alloc — recycle slot)
                if (isFireEdge(i, time, dt)) {
                    const spread = ((i % 5) - 2) * 0.22;
                    spawnTracer(tip.x, tip.y, tip.z, centroidX * 0.2 + slot.x * 0.4 + spread, 0.4 + (i % 3) * 0.08, toZ);
                }
            }
        }
        muzzleMesh.count = muzzleCount;
        muzzleCoreMesh.count = muzzleCount;
        if (muzzleCount > 0) {
            muzzleMesh.instanceMatrix.needsUpdate = true;
            muzzleCoreMesh.instanceMatrix.needsUpdate = true;
        }
        // Material opacity tracks intensity (shared materials)
        muzzleMat.opacity = 0.45 + str * 0.5;
        muzzleCoreMat.opacity = 0.5 + str * 0.45;
        tracerMat.opacity = 0.35 + str * 0.5;
    };
    const dispose = () => {
        muzzleGeo.dispose();
        muzzleCoreGeo.dispose();
        tracerGeo.dispose();
        impactGeo.dispose();
        muzzleMat.dispose();
        muzzleCoreMat.dispose();
        tracerMat.dispose();
        impactMat.dispose();
        group.clear();
    };
    return { group, update, dispose };
}
//# sourceMappingURL=combatFx.js.map